// Admin Bulletins page — three-column layout:
//   left: list of past bulletins (date, counts, sent status)
//   right (top): selected bulletin preview iframe + Send-now / Download .ics
//   right (bottom): recipient management (admin role only)
//
// Uses the same admin token / API helper pattern as admin-campaigns.tsx.

import { useEffect, useState, useMemo } from "react";
import { Mail, Send, Calendar, Plus, Trash2, RefreshCw, AlertCircle, CheckCircle2, Download, Lock } from "lucide-react";

const ADMIN = () => localStorage.getItem("admin_token") || "";

async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", "x-admin-token": ADMIN(), ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const t = await res.text();
    let msg = t;
    try { msg = JSON.parse(t).error || t; } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as any;
  return res.json();
}

interface Bulletin {
  id: number;
  dateIst: string;
  investorCount: number;
  questionCount: number;
  sentAt: string | null;
  createdAt: string;
  recipientsSnapshot: string[];
}

interface Recipient {
  id: number;
  email: string;
  label: string | null;
  addedByTeamMemberId: number | null;
  createdAt: string;
}

// Iframe preview component — keyed by bulletin id by the parent so a new
// instance mounts (and re-fetches the HTML) whenever the user picks a
// different bulletin. Avoids the stale-srcdoc bug from the prior one-shot
// dataset.loaded approach.
function BulletinPreview({ bulletin, onDownload }: { bulletin: Bulletin; onDownload: (b: Bulletin) => void }) {
  const [html, setHtml] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setHtml("");
    setErr(null);
    fetch(`/api/admin/bulletins/${bulletin.id}/preview`, { headers: { "x-admin-token": ADMIN() } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Preview failed (${r.status})`);
        return r.text();
      })
      .then((t) => { if (!cancelled) setHtml(t); })
      .catch((e) => { if (!cancelled) setErr(e.message); });
    return () => { cancelled = true; };
  }, [bulletin.id]);
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <div className="px-4 py-3 bg-muted flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-wider">Preview · {bulletin.dateIst}</div>
        <button
          onClick={() => onDownload(bulletin)}
          className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 border border-border rounded hover:bg-background flex items-center gap-1.5">
          <Download className="w-3.5 h-3.5" /> .ics
        </button>
      </div>
      {err
        ? <div className="p-6 text-center text-sm text-red-700">{err}</div>
        : html
          ? <iframe srcDoc={html} title={`Bulletin ${bulletin.dateIst}`} className="w-full bg-white" style={{ height: 540, border: 0 }} />
          : <div className="p-6 text-center text-sm text-muted-foreground">Loading preview…</div>}
    </div>
  );
}

// Decode the role from the admin token so we can disable recipient mutations
// for non-admin team members. The server still enforces this server-side.
function getMyRole(): string {
  try {
    const tok = ADMIN();
    if (!tok) return "";
    const payload = JSON.parse(atob(tok));
    return payload?.role || "";
  } catch { return ""; }
}

export default function AdminBulletinsPage() {
  const [bulletins, setBulletins] = useState<Bulletin[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selected, setSelected] = useState<Bulletin | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const myRole = useMemo(getMyRole, []);
  const isAdmin = myRole === "admin" || myRole === "master_admin";

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [bres, rres] = await Promise.all([
        api<{ bulletins: Bulletin[] }>("/admin/bulletins"),
        api<{ recipients: Recipient[] }>("/admin/bulletins/recipients/list"),
      ]);
      setBulletins(bres.bulletins);
      setRecipients(rres.recipients);
      if (!selected && bres.bulletins.length > 0) setSelected(bres.bulletins[0]);
    } catch (e: any) {
      setErr(e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function sendNow(force: boolean) {
    setBusy("send");
    setErr(null);
    try {
      const result = await api<{ bulletinId: number; recipientsEmailed: number; totalQuestions: number }>(
        "/admin/bulletins/send-now",
        { method: "POST", body: JSON.stringify({ force }) },
      );
      await load();
      alert(`Sent to ${result.recipientsEmailed} recipient(s) — ${result.totalQuestions} question(s) included.`);
    } catch (e: any) {
      setErr(e.message);
    } finally { setBusy(null); }
  }

  async function addRecipient() {
    if (!newEmail.trim()) return;
    setBusy("add");
    setErr(null);
    try {
      await api("/admin/bulletins/recipients", {
        method: "POST",
        body: JSON.stringify({ email: newEmail.trim(), label: newLabel.trim() || null }),
      });
      setNewEmail("");
      setNewLabel("");
      await load();
    } catch (e: any) {
      setErr(e.message);
    } finally { setBusy(null); }
  }

  async function removeRecipient(id: number) {
    if (!confirm("Remove this recipient from the bulletin list?")) return;
    setBusy(`del-${id}`);
    setErr(null);
    try {
      await api(`/admin/bulletins/recipients/${id}`, { method: "DELETE" });
      await load();
    } catch (e: any) {
      setErr(e.message);
    } finally { setBusy(null); }
  }

  function downloadIcs(b: Bulletin) {
    const url = `/api/admin/bulletins/${b.id}/ics`;
    const a = document.createElement("a");
    a.href = `${url}?token=${encodeURIComponent(ADMIN())}`;
    // Most browsers don't pass our header on a click, so fetch+blob path:
    fetch(url, { headers: { "x-admin-token": ADMIN() } })
      .then(r => r.blob())
      .then(blob => {
        const u = URL.createObjectURL(blob);
        a.href = u;
        a.download = `favcy-bulletin-${b.dateIst}.ics`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(u);
      });
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono uppercase tracking-wider mb-1">
              <Mail className="w-3.5 h-3.5" /> Evening Bulletin
            </div>
            <h1 className="text-2xl font-serif">Daily investor question digest</h1>
            <p className="text-sm text-muted-foreground mt-1">Sent automatically every day at 7:00 PM IST. Recipients are admin-managed.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-border rounded-md hover:bg-accent flex items-center gap-2"
              onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              className="px-3 py-2 text-xs font-mono uppercase tracking-wider bg-[#FF5C00] text-white rounded-md hover:bg-[#e65300] disabled:opacity-50 flex items-center gap-2"
              onClick={() => sendNow(true)} disabled={busy === "send" || recipients.length === 0}
              title={recipients.length === 0 ? "Add recipients first" : "Generate today's bulletin and send now"}>
              <Send className="w-3.5 h-3.5" /> {busy === "send" ? "Sending…" : "Send today now"}
            </button>
          </div>
        </div>

        {err && (
          <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-md text-sm text-red-700 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{err}</span>
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Bulletin list */}
          <div className="col-span-4 space-y-2">
            <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Past bulletins ({bulletins.length})</div>
            {loading && bulletins.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>}
            {!loading && bulletins.length === 0 && (
              <div className="border border-dashed border-border rounded-md p-6 text-center text-sm text-muted-foreground">
                No bulletins yet. The cron runs daily at 7 PM IST, or click <strong className="text-foreground">Send today now</strong> to generate one immediately.
              </div>
            )}
            {bulletins.map((b) => (
              <button
                key={b.id}
                onClick={() => setSelected(b)}
                className={`w-full text-left px-4 py-3 border rounded-md transition ${selected?.id === b.id ? "border-[#FF5C00] bg-[#FF5C00]/5" : "border-border hover:bg-accent"}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-mono text-sm">{b.dateIst}</div>
                  {b.sentAt
                    ? <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 bg-emerald-500/10 text-emerald-700 rounded-full flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Sent</span>
                    : <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 bg-amber-500/10 text-amber-700 rounded-full">Draft</span>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {b.investorCount} investor{b.investorCount === 1 ? "" : "s"} · {b.questionCount} question{b.questionCount === 1 ? "" : "s"}
                </div>
                {b.sentAt && (
                  <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                    sent {new Date(b.sentAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} → {b.recipientsSnapshot?.length || 0} recipient(s)
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Right column: preview + recipients */}
          <div className="col-span-8 space-y-4">
            {selected && (
              <BulletinPreview key={selected.id} bulletin={selected} onDownload={downloadIcs} />
            )}

            {/* Recipient management */}
            <div className="border border-border rounded-md">
              <div className="px-4 py-3 bg-muted flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="text-xs font-mono uppercase tracking-wider">Recipients ({recipients.length})</div>
                  {!isAdmin && (
                    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 bg-amber-500/10 text-amber-700 rounded-full flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Admin only
                    </span>
                  )}
                </div>
              </div>
              <div className="p-4 space-y-3">
                {recipients.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-3">
                    No recipients yet. Add at least one email to receive the daily bulletin.
                  </div>
                )}
                {recipients.map((r) => (
                  <div key={r.id} className="flex items-center justify-between px-3 py-2 border border-border rounded-md">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{r.email}</div>
                      {r.label && <div className="text-xs text-muted-foreground truncate">{r.label}</div>}
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => removeRecipient(r.id)}
                        disabled={busy === `del-${r.id}`}
                        className="text-xs font-mono uppercase tracking-wider px-2 py-1 text-red-600 hover:bg-red-500/10 rounded flex items-center gap-1 disabled:opacity-50">
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    )}
                  </div>
                ))}

                {isAdmin && (
                  <div className="pt-3 border-t border-border">
                    <div className="grid grid-cols-12 gap-2">
                      <input
                        type="email"
                        placeholder="email@favcy.in"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="col-span-5 px-3 py-2 text-sm border border-border rounded-md bg-background"
                      />
                      <input
                        type="text"
                        placeholder="Label (optional)"
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                        className="col-span-5 px-3 py-2 text-sm border border-border rounded-md bg-background"
                      />
                      <button
                        onClick={addRecipient}
                        disabled={busy === "add" || !newEmail.trim()}
                        className="col-span-2 px-3 py-2 text-xs font-mono uppercase tracking-wider bg-[#FF5C00] text-white rounded-md hover:bg-[#e65300] disabled:opacity-50 flex items-center justify-center gap-1">
                        <Plus className="w-3.5 h-3.5" /> Add
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
